import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { useLocation } from 'react-router';
import { useAuth } from '@/lib/auth/auth-context';

const STORAGE_KEY = 'yakson_selected_outlet';

interface OutletContextType {
  /**
   * What the current page is showing: starts at the signed-in outlet every time
   * a page is opened (GEN-01-03 item 4) and is changed only by the page's own
   * Outlet filter (PageOutletFilter). null means "All outlets".
   */
  selectedOutlet: string | null;
  /** Page-level only. Never changes the signed-in outlet (GEN-01-07). */
  setSelectedOutlet: (outlet: string | null) => void;
  /**
   * The outlet of THIS sign-in (null = HQ / All outlets). Fixed until sign-out;
   * the header's 'Signed in: <outlet>' label reads it. Only the sign-in screen
   * sets it.
   */
  signedInOutlet: string | null;
  setSignedInOutlet: (outlet: string | null) => void;
  /**
   * True when the signed-in user is pinned to one outlet and cannot switch the
   * page's Outlet filter (permissions stay as they are, GEN-01-07).
   */
  isOutletLocked: boolean;
}

const OutletContext = createContext<OutletContextType | undefined>(undefined);

export function OutletProvider({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  const { pathname } = useLocation();

  /**
   * HQ (ADMIN) roams: it signs in as "All Outlets" or as one outlet chosen on
   * the Sign In screen. Everyone else is locked to the outlet on their linked
   * staff record — the `outletId` JWT claim. A non-admin with no staff link has
   * no outlet to lock to, so it stays unlocked.
   */
  const isOutletLocked = Boolean(user && user.userType !== 'ADMIN' && user.outletId);

  const [signedInOutlet, setSignedInOutletRaw] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) ?? null;
    } catch {
      return null;
    }
  });
  // A page's own Outlet filter: undefined = not touched on this page yet, so the
  // page shows the signed-in outlet.
  const [pageOutlet, setPageOutlet] = useState<{ path: string; outlet: string | null } | null>(null);

  const setSignedInOutlet = useCallback((outlet: string | null) => {
    setSignedInOutletRaw(outlet);
    setPageOutlet(null);
    try {
      if (outlet) {
        localStorage.setItem(STORAGE_KEY, outlet);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch { /* localStorage unavailable */ }
  }, []);

  useEffect(() => {
    // Auth bootstraps asynchronously: `user` is null before the token is read.
    // Acting on that would wipe the persisted sign-in outlet on every page load.
    if (isLoading) return;

    if (!user) {
      setSignedInOutlet(null);
      return;
    }

    // The staff record wins over anything persisted locally — including a value
    // left behind by whoever used this browser last.
    if (isOutletLocked && signedInOutlet !== user.outletId) {
      setSignedInOutlet(user.outletId ?? null);
    }
  }, [isLoading, user, isOutletLocked, signedInOutlet, setSignedInOutlet]);

  // A filter chosen on one page does not follow staff to the next page: every
  // page opens at the signed-in outlet.
  const selectedOutlet =
    pageOutlet && pageOutlet.path === pathname ? pageOutlet.outlet : signedInOutlet;

  const setSelectedOutlet = useCallback(
    (outlet: string | null) => {
      if (isOutletLocked) return;
      setPageOutlet({ path: pathname, outlet });
    },
    [isOutletLocked, pathname],
  );

  const value = useMemo(
    () => ({ selectedOutlet, setSelectedOutlet, signedInOutlet, setSignedInOutlet, isOutletLocked }),
    [selectedOutlet, setSelectedOutlet, signedInOutlet, setSignedInOutlet, isOutletLocked],
  );

  return <OutletContext.Provider value={value}>{children}</OutletContext.Provider>;
}

export function useOutlet() {
  const context = useContext(OutletContext);
  if (context === undefined) {
    throw new Error('useOutlet must be used within an OutletProvider');
  }
  return context;
}
